import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { blobPath } from 'constants/constant';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/sharp-duotone-solid-svg-icons';
import withErrorBoundary from './withErrorBoundary';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MainContext } from 'contexts/MainContext';
import { imgLoaderMain, defaultLoaderUrl } from 'constants/generic';

interface LoaderMainProps {
  systemProducts:
  | 'Dynamic_FMHM'
  | 'Dynamic'
  | 'Hotels'
  | 'Flights'
  | 'Transfers'
  | 'Nofshon'
  | 'Tours';
  systemProductsDetails?: 'LoadingBook' | 'LoadingDetails';
}

interface LoadingItem {
  loadingPage: {
    systemProducts: string | string[];
    systemProductsDetails?: string;
    systemBasic?: string;
    listOfGalleryId?: Array<{ code: string }>;
    webKey?: string;
    languages?: {
      language?: string;
      title?: {
        desc?: string;
        sub?: string;
        body?: string;
      };
      title_2?: {
        body?: string;
        desc?: string;
        sub?: string;
      };
    }[];
  };
  pageContent?: {
    desc?: string;
    sub?: string;
    body?: string;
  };
}

const LoaderMain: FC<LoaderMainProps> = ({ systemProducts, systemProductsDetails }) => {
  const [loading, setLoading] = useState<LoadingItem | undefined>();
  const [imageError, setImageError] = useState(false);
  const {
    cdnUrl,
    isEnigma,
    initSiteData
  } = useContext(MainContext);
  const { i18n } = useTranslation();
  const location = useLocation();
  const allLoading: { loadings: LoadingItem[] } | undefined = JSON.parse(
    localStorage.getItem('allLoadings')!
  );

  const isImage = !!loading?.loadingPage?.listOfGalleryId?.length;
  const isContent = !!loading?.pageContent?.desc || !!loading?.pageContent?.body;

  const findLoadingByExactMatch = (items: LoadingItem[]) => {
    return items.find(
      (item) =>
        item.loadingPage.systemProducts === systemProducts &&
        item.loadingPage.systemProductsDetails === systemProductsDetails
    );
  };

  const findLoadingByIncludes = (items: LoadingItem[]) => {
    return items.find((item) =>
      Array.isArray(item.loadingPage.systemProducts)
        ? item.loadingPage.systemProducts.includes(systemProducts)
        : false && item.loadingPage.systemProductsDetails === systemProductsDetails
    );
  };

  const findDefaultModuleLoading = (items: LoadingItem[]) => {
    const exactMatch = items.find(
      (item) =>
        item.loadingPage.systemProducts === systemProducts &&
        item.loadingPage.systemProductsDetails === 'Loading'
    );
    const includesMatch = items.find((item) =>
      Array.isArray(item.loadingPage.systemProducts)
        ? item.loadingPage.systemProducts.includes(systemProducts)
        : false && item.loadingPage.systemProductsDetails === 'Loading'
    );
    return exactMatch || includesMatch;
  };

  const findDefaultLoading = (items: LoadingItem[]) => {
    return items.find((item) => item.loadingPage.systemBasic === 'Loading');
  };

  useEffect(() => {
    if (allLoading?.loadings) {
      const exactMatch = findLoadingByExactMatch(allLoading.loadings);
      const includesMatch = findLoadingByIncludes(allLoading.loadings);
      const defaultModule = findDefaultModuleLoading(allLoading.loadings);

      if (exactMatch || includesMatch) {
        setLoading(exactMatch || includesMatch);
      } else if (defaultModule) {
        setLoading(defaultModule);
      } else {
        const defaultLoading = findDefaultLoading(allLoading.loadings);
        setLoading(defaultLoading);
      }
    }
  }, []); // eslint-disable-line

  if (allLoading?.loadings?.[0]?.loadingPage?.webKey?.includes('NETRAVELHE')) {
    if (location.pathname.includes('result')) {
      return (
        <div className="loader-main-nertravel">
          <div className="title text-black text-center">
            {loading?.loadingPage.languages?.[0]?.title_2?.desc}
          </div>
          <div className="sub">{loading?.loadingPage.languages?.[0]?.title_2?.sub}</div>
          <div
            dangerouslySetInnerHTML={{
              __html: loading?.loadingPage.languages?.[0]?.title_2?.body || '',
            }}
          ></div>

          <img
            className="spinner"
            src={cdnUrl + blobPath + '/s3/prod.beprotravel/cdn/frontend/loadernetravel.gif'}
            alt="nertravel"
          />
        </div>
      );
    } else {
      return null;
    }
  }

  const findLang = () => {
    const result = loading?.loadingPage?.languages?.find(
      (item) => item.language?.toLowerCase() === i18n.language.toLowerCase()
    );
    return result;
  };

  return (
    <Fragment>
      {!imgLoaderMain && (isImage || isContent) && (
        <div className={'loader-main' + (isContent ? '' : ' loader-main-nocontent')}>
          <div className="loader-main-content default-shadow">
            {isContent && loading?.pageContent && (
              <div className="text-lh">
                <div className="title">{findLang()?.title?.desc || loading.pageContent.desc}</div>
                <div className="mb-5">{findLang()?.title?.sub || loading.pageContent.sub}</div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: findLang()?.title?.body || loading.pageContent.body || '',
                  }}
                ></div>
              </div>
            )}

            {isImage && !imageError && loading?.loadingPage?.listOfGalleryId?.[0]?.code && (
              <img
                src={cdnUrl + blobPath + loading.loadingPage.listOfGalleryId[0].code}
                alt="load"
                onError={() => setImageError(true)}
              />
            )}
          </div>
        </div>
      )}
      {imgLoaderMain && (
        <div className="loader-main loader-main-nocontent">
          <div className="loader-main-content default-shadow">
            <img
              alt="logo"
              src={(cdnUrl || '') + (isEnigma ? initSiteData?.other?.logoUrl || '' : initSiteData?.other?.mainLoaderUrl || initSiteData?.other?.defaultLoaderUrl || defaultLoaderUrl || '')}
            />
            <FontAwesomeIcon icon={faSpinnerThird as any} className="bp-spin" />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(LoaderMain, 'LoaderMain');
